import * as React from "react";

import { useLazyGetShopifyProductsQuery } from "@editor/reducers/api-reducer";
import useCurrentProjectId from "@hooks/useCurrentProjectId";

import { skipToken, useInfiniteQuery } from "@tanstack/react-query";

/**
 * Hook to fetch product data for the current store.
 */
export function useInfiniteStoreProducts(opts: {
  pageSize: number;
  searchTerm?: string;
}) {
  const { pageSize, searchTerm } = opts;
  const [triggerGetProductsQuery] = useLazyGetShopifyProductsQuery();
  const triggerFetchProductsQuery = async (
    projectId: string,
    nextPage: string | undefined,
    searchTerm?: string,
  ) => {
    const response = await triggerGetProductsQuery(
      {
        projectId,
        pageSize,
        nextPage,
        type: "search",
        searchTerm: searchTerm ?? "",
      },
      true,
    )
      .unwrap()
      .catch(() => null);
    return response;
  };

  const projectId = useCurrentProjectId();
  const {
    data,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
    error,
    isFetching,
  } = useInfiniteQuery({
    queryKey: ["products", searchTerm],
    queryFn: projectId
      ? async ({ pageParam }: { pageParam: string | null }) => {
          return await triggerFetchProductsQuery(
            projectId,
            pageParam ?? undefined,
            searchTerm,
          );
        }
      : skipToken,
    getNextPageParam: (lastPage) => {
      return lastPage?.pageInfo?.nextPage;
    },
    initialPageParam: null,
  });

  return {
    fetchNextPage,
    products: data?.pages.map((page) => page?.products ?? []).flat() ?? [],
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    error,
  };
}
